import { User } from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { createContext, useContext } from 'react';

import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCurrentUser } from 'utils/user';

import { RewindDataNormalized } from './hooks';

type RewindContextValue = {
  /** The normalized rewind data from the API */
  data: RewindDataNormalized | null;
  /** Whether data is currently being fetched */
  isLoading: boolean;
  /** Whether an error occurred during fetch */
  isError: boolean;
  /** The user the data was fetched for */
  user: User;
};

const RewindContext = createContext<RewindContextValue | null>(null);

type RewindProviderProps = Omit<RewindContextValue, 'user'> & {
  userId: string;
} & PropsWithChildren;

export function RewindProvider({
  children,
  data,
  isLoading,
  isError,
  userId,
}: RewindProviderProps): JSX.Element {
  const { user: currentUser } = useCurrentUser();
  const allPresence = useAppSelector((state) => state.presence.allPresence);

  const user =
    currentUser.id === userId
      ? currentUser
      : allPresence.find((p) => p.user.id === userId)?.user ||
        ({
          id: userId,
          name: 'Agent',
          given_name: 'Agent',
          family_name: '',
        } as User);

  return (
    <RewindContext.Provider value={{ data, user, isLoading, isError }}>
      {children}
    </RewindContext.Provider>
  );
}

export function useRewindContext(): RewindContextValue {
  const context = useContext(RewindContext);

  if (!context) {
    throw new Error(
      'useRewindDataContext must be used within a RewindDataProvider',
    );
  }

  return context;
}
